import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import {
  Accordion, AccordionDetails, AccordionSummary, AppBar, Box, Button, Card, CardContent, Container, Paper, Stack, Toolbar, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import Footer from './Footer';
import ThemeModeToggle from './ThemeModeToggle';
import PlanComparison from './PlanComparison';
import { Brand } from './Home';
import { LANDING_LINKS } from '../landingPages';

/**
 * One of the public pages written for a search people make (AI voice interview software, resume screening, the job application tracker,
 * AI practice interviews, pricing). They are all drawn here from one shape of data, which the server owns: it is already in the page
 * when this is opened directly (`window.__PAGE__`, put there by services/marketingPages.js), and is fetched from /api/public/page when
 * this is reached by a link inside the app. So the words are written once, and what a crawler reads is what is drawn.
 */

const labelFor = (path) => LANDING_LINKS.find(([route]) => route === path)?.[1] || path;

const SectionText = ({ section }) => (
  <>
    {(section.paragraphs || []).map((text) => (
      <Typography key={text} color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>{text}</Typography>
    ))}
    {section.bullets && (
      <Box component="ul" sx={{ pl: 3, m: 0, '& li': { mb: 0.75, color: 'text.secondary', lineHeight: 1.6 } }}>
        {section.bullets.map((text) => <li key={text}>{text}</li>)}
      </Box>
    )}
    {section.numbered && (
      <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 1.25, color: 'text.secondary', lineHeight: 1.6 } }}>
        {section.numbered.map((step) => (
          <li key={step.title}><Box component="strong" sx={{ color: 'text.primary' }}>{step.title}.</Box> {step.text}</li>
        ))}
      </Box>
    )}
    {section.faq && section.faq.map((item) => (
      <Accordion key={item.q} variant="outlined" disableGutters sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="h3" sx={{ fontWeight: 600 }}>{item.q}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>{item.a}</Typography>
        </AccordionDetails>
      </Accordion>
    ))}
    {section.plans && (
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' } }}>
        {section.plans.map((plan) => (
          <Card key={plan.name} variant="outlined" data-plan={plan.name}>
            <CardContent>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>{plan.name}</Typography>
              {plan.price && (
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="h4" component="p" sx={{ fontWeight: 700 }}>{plan.price}</Typography>
                  <Typography color="text.secondary">{plan.cadence}</Typography>
                </Stack>
              )}
              <Typography color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>{plan.summary}</Typography>
              {plan.points && (
                <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 1.5, '& li': { color: 'text.secondary' } }}>
                  {plan.points.map((point) => <li key={point}>{point}</li>)}
                </Box>
              )}
              <Button component={RouterLink} to={plan.to} variant={plan.name === 'Premium' ? 'contained' : 'outlined'} fullWidth>
                {plan.label || (plan.name === 'Free' ? 'Create a free account' : plan.name === 'Premium' ? 'Start with Premium' : 'Set up your organization')}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>
    )}
  </>
);

const LandingPage = ({ path }) => {
  const injected = typeof window !== 'undefined' && window.__PAGE__ && window.__PAGE__.path === path ? window.__PAGE__.page : null;
  const [page, setPage] = useState(injected);
  const [failed, setFailed] = useState(false);
  const [limits, setLimits] = useState(null);

  useEffect(() => {
    // Reached by a link inside the app: the words were not in the page, so ask for them.
    if (page && page === injected) return undefined;
    let cancelled = false;
    setPage(null);
    setFailed(false);
    axios.get('/api/public/page', { params: { path } })
      .then((response) => { if (!cancelled) setPage(response.data?.page || null); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    if (page?.title) document.title = page.title;
  }, [page]);

  // The pricing page shows the same side-by-side table as the home page, which needs the limits the server publishes.
  useEffect(() => {
    if (path !== '/pricing') return undefined;
    let cancelled = false;
    axios.get('/api/public/signup/pricing')
      .then((response) => { if (!cancelled) setLimits(response.data?.limits || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [path]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ gap: 1, flexWrap: 'wrap', py: 0.5 }}>
          <Box component={RouterLink} to="/" aria-label="Home" sx={{ flexGrow: 1, minWidth: 0, textDecoration: 'none', color: 'inherit' }}><Brand /></Box>
          <ThemeModeToggle />
          <Button component={RouterLink} to="/login" color="inherit" sx={{ whiteSpace: 'nowrap' }}>Sign in</Button>
          <Button component={RouterLink} to="/signup" variant="contained" sx={{ whiteSpace: 'nowrap' }}>Get started</Button>
        </Toolbar>
      </AppBar>

      <Container component="main" maxWidth="md" sx={{ flex: 1, py: { xs: 3, md: 5 } }}>
        {failed && <Typography color="text.secondary">This page could not be loaded. Please try again.</Typography>}
        {!page && !failed && <Box aria-busy sx={{ minHeight: 240 }} />}
        {page && (
          <>
            <Box component="nav" aria-label="Breadcrumb" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <RouterLink to="/" style={{ color: 'inherit' }}>Home</RouterLink>{' / '}{page.crumb}
              </Typography>
            </Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, lineHeight: 1.15, mb: 2 }}>{page.h1}</Typography>
            <Typography variant="h6" component="p" color="text.secondary" sx={{ fontWeight: 400, lineHeight: 1.6, mb: 3 }}>{page.intro}</Typography>

            {page.sections.map((section) => (
              <Paper key={section.heading} component="section" variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: 2 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>{section.heading}</Typography>
                <SectionText section={section} />
                {path === '/pricing' && section.heading === 'What Premium adds' && <PlanComparison limits={limits} />}
              </Paper>
            ))}

            {page.cta && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" alignItems="center" sx={{ my: 4 }}>
                <Button component={RouterLink} to={page.cta.to} variant="contained" size="large">{page.cta.label}</Button>
                {page.secondaryCta && (
                  <Button component={RouterLink} to={page.secondaryCta.to} variant="outlined" size="large">{page.secondaryCta.label}</Button>
                )}
              </Stack>
            )}

            {page.related?.length > 0 && (
              <Box component="section" aria-labelledby="related-heading">
                <Typography id="related-heading" variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>Related</Typography>
                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                  {page.related.map((route) => <li key={route}><RouterLink to={route}>{labelFor(route)}</RouterLink></li>)}
                </Box>
              </Box>
            )}
          </>
        )}
      </Container>

      <Footer />
    </Box>
  );
};

export default LandingPage;
